import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField, useVibesites } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { formatBedroomsLabel } from '../utils/formatBedrooms';

const FLOOR_PLANS_PATH = '/floor-plans';
const INTERACTIVE_MAP_PATH = '/interactive-map';

function hasCustomFieldValue(val: unknown): boolean {
  if (val == null) return false;
  const normalized = String(val).trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  const { pagePaths } = useVibesites();

  const mapJavascriptId = usePropertyField('custom_apartmentsync_javascript_id');
  const mapPageLive = !pagePaths || pagePaths.includes(INTERACTIVE_MAP_PATH);
  const hasActiveMap = hasCustomFieldValue(mapJavascriptId) && mapPageLive;
  const defaultCtaHref = hasActiveMap ? INTERACTIVE_MAP_PATH : FLOOR_PLANS_PATH;
  // Schema default stays /floor-plans for company preview. At render, that
  // default (or an empty URL) swaps to the interactive leasing page when the
  // property has an ApartmentSync map. Any other editor-set URL still wins.
  const ctaHref =
    ctaLink.url && ctaLink.url !== FLOOR_PLANS_PATH ? ctaLink.url : defaultCtaHref;

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    const bedsPrefix = beds ? `${beds} ` : '';
    return `<p>${bedsPrefix}Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <section className="relative w-full h-[75vh] md:h-screen min-h-[400px] md:min-h-[600px] max-h-[1000px] overflow-hidden">
      {/* Ken Burns animated background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-1 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl mb-10 leading-relaxed opacity-90 drop-shadow-md"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaHref}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2 sm:gap-2.5 whitespace-nowrap px-6 py-3 sm:px-10 sm:py-4 text-xs sm:text-sm font-semibold tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
            <svg className="relative z-10 w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.2s; }
        .hero-entrance-2 { animation-delay: 0.45s; }
        .hero-entrance-3 { animation-delay: 0.7s; }
        .hero-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .hero-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          left: 100%;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Welcome to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
